/**
 * Agent Theme Mapping
 *
 * Maps copilot agent types to theme modes.
 * This is a bridge file between the copilot feature and the theme system.
 */

import type { ThemeMode } from '@/lib/tokens/semantic'
import { AgentType } from 'sprites-types/api/v2/copilot'

// Map agent types to theme modes
export const AGENT_THEME_MAP: Record<AgentType, ThemeMode> = {
  [AgentType.META_ADS]: 'warm',
  [AgentType.GOOGLE_ADS]: 'green',
  [AgentType.SEO]: 'blue',
  [AgentType.SOCIAL_MEDIA]: 'purple',
}

/**
 * Get theme mode for a given agent type
 */
export function getThemeForAgent(agentType: AgentType): ThemeMode {
  return AGENT_THEME_MAP[agentType]
}

/**
 * Parse hex color to RGB components
 */
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return [0, 0, 0]
  return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
}

/**
 * Format RGB components to hex color
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => Math.round(x).toString(16).padStart(2, '0')).join('')
}

/**
 * Interpolate between two colors
 */
export function interpolateColor(color1: string, color2: string, t: number): string {
  const [r1, g1, b1] = hexToRgb(color1)
  const [r2, g2, b2] = hexToRgb(color2)

  const r = r1 + (r2 - r1) * t
  const g = g1 + (g2 - g1) * t
  const b = b1 + (b2 - b1) * t

  return rgbToHex(r, g, b)
}

/**
 * Easing function for smooth transitions
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

// Re-export ThemeMode for backwards compatibility
export type { ThemeMode }
export type AccentColor = ThemeMode

// Deprecated: Use ThemeMode instead
export const getAccentForAgent = getThemeForAgent
